import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import Reveal from '../Reveal';
import { GOOGLE_PROFILE_URL, GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from '../../siteConfig';

const RATINGS = [5, 5, 4];

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.57-5.17 3.57-8.81z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.88-3c-1.07.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.29 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.28a12 12 0 0 0 0 10.76l4.01-3.1z" />
      <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.5 11.5 0 0 0 12 0 12 12 0 0 0 1.28 6.62l4.01 3.1C6.23 6.88 8.88 4.77 12 4.77z" />
    </svg>
  );
}

function Stars({ count, size = 18, label }) {
  return (
    <span className="inline-flex gap-0.5 text-taxi" role="img" aria-label={label}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} aria-hidden="true" fill={i < count ? 'currentColor' : 'none'} strokeWidth={1.75} />
      ))}
    </span>
  );
}

function Reviews() {
  const { t } = useTranslation();
  const reviews = t('home.testimonials.items', { returnObjects: true });
  const showGoogle = GOOGLE_RATING && GOOGLE_REVIEW_COUNT && GOOGLE_PROFILE_URL;

  return (
    <section id="reviews" className="bg-navy py-16 font-sans text-white lg:py-24">
      <div className="site-container">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="m-0 text-[28px] font-bold leading-tight tracking-tight lg:text-[44px]">
            {t('home.testimonials.title')}
          </h2>
          {showGoogle && (
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="focus-ring-dark inline-flex min-h-[44px] items-center gap-3 self-start rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy no-underline shadow-lg transition-transform hover:-translate-y-0.5 sm:self-auto"
            >
              <GoogleLogo />
              <span>{t('site.reviews.google', { rating: GOOGLE_RATING, count: GOOGLE_REVIEW_COUNT })}</span>
            </a>
          )}
        </div>

        <ul className="m-0 mt-10 grid list-none gap-5 p-0 lg:grid-cols-3 lg:gap-6">
          {reviews.map((review, i) => (
            <Reveal as="li" key={review.name} delay={i * 60}>
              <figure className="card-lift m-0 h-full rounded-card border-0 border-l-4 border-solid border-taxi bg-white/[0.06] p-6 lg:p-8">
                <Stars count={RATINGS[i]} label={t('site.reviews.stars', { count: RATINGS[i] })} />
                <blockquote className="m-0 mt-4 text-base leading-relaxed text-white lg:text-lg">
                  “{review.text}”
                </blockquote>
                <figcaption className="mt-5 font-semibold text-taxi">{review.name}</figcaption>
              </figure>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default Reviews;
